"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const jsx_runtime_1 = require("react/jsx-runtime");
// @ts-ignore-next-line importsNotUsedAsValues
const react_1 = require("react");
const react_dom_1 = tslib_1.__importDefault(require("react-dom"));
const classnames_1 = tslib_1.__importDefault(require("classnames"));
const noop_1 = tslib_1.__importDefault(require("lodash/fp/noop"));
const framer_motion_1 = require("framer-motion");
const portalRoot_1 = require("../../utils/portalRoot");
const useEsc_1 = tslib_1.__importDefault(require("../../hooks/useEsc"));
require("../../hooks/useAfterMount");
const DialogHeader_1 = tslib_1.__importDefault(require("./DialogHeader"));
const DialogBody_1 = tslib_1.__importDefault(require("./DialogBody"));
const DialogFooter_1 = tslib_1.__importDefault(require("./DialogFooter"));
const useFocusTrap_1 = tslib_1.__importDefault(require("../../hooks/useFocusTrap"));
const dialogContext_1 = require("./dialogContext");
const MODAL_DIALOG_CLASS = 'modal-dialog';
const MODAL_OPEN_CLASS = 'modal-open';
const Dialog = (props) => {
    const { title, subtitle, body, footer, headerButtons, className = '', bodyClassName, footerClassName, showXsDialogBorders = false, showCloseButton = true, useOverflow = false, bsSize, show = false, onHide = noop_1.default, onClose, disableEsc = false, onEsc = noop_1.default, onCloseValidation = () => true, children } = props, remainingProps = tslib_1.__rest(props, ["title", "subtitle", "body", "footer", "headerButtons", "className", "bodyClassName", "footerClassName", "showXsDialogBorders", "showCloseButton", "useOverflow", "bsSize", "show", "onHide", "onClose", "disableEsc", "onEsc", "onCloseValidation", "children"]);
    const [open, setOpen] = (0, react_1.useState)(show);
    const dialogWrapperRef = (0, react_1.useRef)(null);
    const onCloseCallback = onClose !== null && onClose !== void 0 ? onClose : onHide;
    const modalRoot = (0, portalRoot_1.getOrCreatePortalRoot)();
    // Use a setter function for the focus trap as it would not re-render
    // and fails to set the focus listener
    const [focusTrapRef, setFocusTrapRef] = (0, react_1.useState)();
    (0, useFocusTrap_1.default)(focusTrapRef);
    const toggleBodyClass = (add) => {
        // We need to set a body class to fix the -webkit-overflow-scrolling on safari and iOS
        // Remove "modal-open" from body only when there is no other dialog in the DOM
        // in order to support multiple dialogs
        const hasOtherDialogs = modalRoot.getElementsByClassName(MODAL_DIALOG_CLASS).length > 1;
        if (add) {
            document.body.classList.add(MODAL_OPEN_CLASS);
        }
        else if (!add && !hasOtherDialogs) {
            document.body.classList.remove(MODAL_OPEN_CLASS);
        }
    };
    const handleCloseButton = () => closeDialog(true);
    const closeDialog = (usedEscapeKey) => {
        if (onCloseValidation()) {
            setOpen(false);
            onCloseCallback();
            usedEscapeKey && onEsc();
        }
    };
    const [previousShow, setPreviousShow] = (0, react_1.useState)(show);
    if (show !== previousShow) {
        setOpen(show);
        toggleBodyClass(show);
        setPreviousShow(show);
    }
    (0, useEsc_1.default)(() => {
        if (!dialogWrapperRef || !dialogWrapperRef.current) {
            return;
        }
        const dialogElement = dialogWrapperRef.current;
        const currentActiveElement = document.activeElement;
        // Only allow to close the dialog when the focus is inside the dialog
        if (!disableEsc && dialogElement.contains(currentActiveElement)) {
            closeDialog(true);
        }
    });
    const handleAnimationComplete = (definition) => {
        var _a;
        // The animation complete callback is invoked too when closing the dialog.
        // For that we check the animation props like the "opacity" to skip focusing
        // on the way out when closing the dialog.
        // @ts-ignore
        if (definition.opacity === 0) {
            return;
        }
        const dialogElement = dialogWrapperRef.current;
        const currentActiveElement = document.activeElement;
        // Set the focus to the dialog if no element inside has focus already. Otherwise, focused
        // elements like inputs would lose their focus to the dialog.
        // Note that in order to focus the dialog itself, the tabindex has to be set on that element
        // IMPORTANT: make sure the dialog has a tabIndex prop
        if (!(dialogElement === null || dialogElement === void 0 ? void 0 : dialogElement.contains(currentActiveElement))) {
            (_a = dialogWrapperRef === null || dialogWrapperRef === void 0 ? void 0 : dialogWrapperRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    const modalClasses = (0, classnames_1.default)('modal', 'show', className);
    const isSmallestDialog = bsSize === 'xs';
    const hasChildren = !!children;
    const modalDialogClasses = (0, classnames_1.default)(MODAL_DIALOG_CLASS, useOverflow && 'modal-overflow', bsSize === 'xs' && 'modal-xs', bsSize === 'sm' && 'modal-sm', bsSize === 'lg' && 'modal-lg', bsSize === 'xl' && 'modal-xl', bsSize === 'full' && 'modal-full-width', bsSize === 'fullwidth' && 'modal-full-width', bsSize === 'fullheight' && 'modal-full-height', bsSize === 'fullscreen' && 'modal-fullscreen');
    const spring = {
        type: 'spring',
        damping: 33,
        stiffness: 500,
    };
    const springXs = {
        type: 'spring',
        damping: 25,
        stiffness: 400,
    };
    return react_dom_1.default.createPortal((0, jsx_runtime_1.jsx)(dialogContext_1.DialogContextProvider, Object.assign({ value: { onClose: handleCloseButton, isSmallestDialog, showXsDialogBorders, showCloseButton } }, { children: (0, jsx_runtime_1.jsx)(framer_motion_1.AnimatePresence
        // Disable any initial animations on children that
        // are present when the component is first rendered
        , Object.assign({ 
            // Disable any initial animations on children that
            // are present when the component is first rendered
            initial: false, 
            // Only render one component at a time.
            // The exiting component will finish its exit
            // animation before entering component is rendered
            exitBeforeEnter: true }, { children: open && ((0, jsx_runtime_1.jsxs)("div", Object.assign({}, remainingProps, { className: modalClasses, role: 'dialog', ref: dialogWrapperRef, "aria-label": 'dialog', 
                // Make sure it has a tabIndex to focus the dialog so the close on esc works.
                // biome-ignore lint/a11y/noNoninteractiveTabindex: <explanation>
                tabIndex: 0 }, { children: [(0, jsx_runtime_1.jsx)(framer_motion_1.motion.div, Object.assign({ initial: { opacity: 0, y: '-50%' }, animate: { opacity: 1, y: 0, transition: isSmallestDialog ? springXs : spring }, exit: { opacity: 0, y: '-150%' }, transition: { opacity: 0.2, y: 0.3 }, onAnimationComplete: handleAnimationComplete, 
                        // @ts-ignore
                        ref: setFocusTrapRef, className: modalDialogClasses, role: 'document' }, { children: (0, jsx_runtime_1.jsxs)("div", Object.assign({ className: 'modal-content' }, { children: [hasChildren && children, !hasChildren && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [title && ((0, jsx_runtime_1.jsx)(DialogHeader_1.default, { title: title, subtitle: subtitle, headerButtons: headerButtons })), body && (0, jsx_runtime_1.jsx)(DialogBody_1.default, Object.assign({ className: bodyClassName }, { children: body })), footer && (0, jsx_runtime_1.jsx)(DialogFooter_1.default, Object.assign({ className: footerClassName }, { children: footer }))] }))] })) })), (0, jsx_runtime_1.jsx)(framer_motion_1.motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 }, className: 'modal-backdrop' })] }))) })) })), modalRoot);
};
Dialog.Title = DialogHeader_1.default;
Dialog.Body = DialogBody_1.default;
Dialog.Footer = DialogFooter_1.default;
Dialog.SIZE_XS = 'xs';
Dialog.SIZE_SM = 'sm';
Dialog.SIZE_MD = 'md'; // default
Dialog.SIZE_LG = 'lg';
Dialog.SIZE_XL = 'xl';
Dialog.SIZE_FULL = 'full';
Dialog.SIZE_FULL_WIDTH = 'fullwidth';
Dialog.SIZE_FULL_HEIGHT = 'fullheight';
Dialog.SIZE_FULL_SCREEN = 'fullscreen';
exports.default = Dialog;
